<template>
    <div
        :style="$_getStyle"
        class="filter-detail shadowSmall"
        :class="getObjectDetailClass">
        <!-- Header -->
        <div class="panel-header modeless">
            <h3 class="panel-header-title crop modeless ">{{$t('labelFilters')}}</h3>
            <!-- Close icon -->
            <icon-close id="panel-filter-close"
                        v-if="filterPanel.components.close.visible"
                        @close="togglePanel({panelName: 'filters', visible: false})"
                        iconColor=""
                        class="d-inline-block float-right">
            </icon-close>
        </div>
        <!-- Content -->
        <div class="panel-content-sidebar">
            <div class="filtercontainer">
                <b-container v-if="filterData.length < 1">
                    <span>{{ $t('noFilterResultMessageWeb') }}</span>
                </b-container>
                <b-form v-else ref="filterForm" id="filter-form" name="filter-form" enctype="multipart/form-data" v-on:submit.prevent="$_applyFilters">
                    <b-container>
                        <b-row class="ml-0" role="tablist">
                            <b-col class="pl-0">
                                <b-card no-body class="mb-1" v-for="themelayer in filterData" :key="themelayer.customId">
                                    <layer-filters
                                        :themeLayer="themelayer"
                                        :appliedFilters="appliedFilters"
                                        :isExpanded="true"
                                        @update="$_updatedFilter($event)"
                                    />
                                </b-card>
                            </b-col>
                        </b-row>
                        <b-row class="mt-4">
                            <b-col>
                                <x-checkbox
                                    id="cbx_showFilterSelectionInPanel"
                                    name="cbx_showFilterSelectionInPanel"
                                    v-model="showFilterSelectionInPanel"
                                    :text="$t('selectItems')"
                                    class="text-nowrap">
                                </x-checkbox>
                            </b-col>
                            <b-col class="text-right">
                                <b-button
                                    variant="primary"
                                    type="submit"
                                    :class="{ 'saving': isSaving }"
                                >
                                    <span>{{$t('apply')}}</span>
                                </b-button>
                            </b-col>
                        </b-row>
                    </b-container>
                 </b-form>
                <pre v-if="$store.getters.showDevInfo">{{appliedFilters}}</pre>
             </div>
        </div>
    </div>
</template>

<script>
    import { mapState, mapActions, mapGetters } from 'vuex';
    import { DOMAIN_TYPE, INTERFACE_PANEL_TRIGGERS, SELECTION_TYPES, MENUBAR_HEIGHT } from '../../../../constants';
    import { arraySortAscendingFunction } from '../../../../services/utilities';
    import { expandFilterValue } from '../../../../services/map/filter';
    import IconClose from '../../../IconClose.vue';
    import LayerFilters from './LayerFilters.vue';

    export default {
        name: 'filter-selection',

        components: {
            'icon-close': IconClose,
            'layer-filters': LayerFilters
        },

        data() {
            return {
                isSaving: false,
                appliedFilters: [],
                showFilterSelectionInPanel: false
            };
        },

        watch: {
            selectedLayersIds: function (newVal, oldVal) {
                const addedIds = newVal.filter(x => !oldVal.includes(x));
                const removedIds = oldVal.filter(x => !newVal.includes(x));
                const filtersToApply = this.appliedFilters.filter(({ layerId }) => !removedIds.find(id => id === layerId));

                addedIds.forEach(id => {
                    const layerObject = this.getLayerById(id);
                    layerObject.filters.settings.forEach(setting => {
                        if (setting.data.filter.domainType === DOMAIN_TYPE.ALL_VALUES) {
                            setting.data.filter.value = setting.data.filter.feedbackDomain.keyvalues.map(({ value }) => value);
                        }
                        const filterToApply = {
                            themeId: layerObject.data.themeId,
                            layerId: id,
                            filterId: setting.data.filterId,
                            filter: setting.data.filter
                        };
                        filtersToApply.push(filterToApply);
                    });
                });
                this.appliedFilters = filtersToApply;
            }
        },

        computed: {
            ...mapGetters(['getLayerById', 'getActivatedLayers', 'getWebTheme', 'getDetailInformationPanelHeight']),
            ...mapState(['uiElements', 'context']),
            filterPanel: function() {
                return this.uiElements[INTERFACE_PANEL_TRIGGERS.FILTERS];
            },
            selectedLayersIds: function() {
                return this.getActivatedLayers.map(({ id }) => id);
            },
            selectedThemes: function() {
                const theme = this.getWebTheme;
                if (!theme) {
                    return undefined;
                }
                const { name, id } = theme.data;
                return [{ name, id, layers:  this.getActivatedLayers.filter(({ type }) => type === 'theme') }];
            },
            filterData: function() {
                if (!this.selectedThemes) {
                    return [];
                }
                // for all active themes
                return this.selectedThemes
                    .map(theme => ({
                        ...theme,
                        layers: theme.layers
                            .map((layerObject) => {
                                const { id, title, themeId } = layerObject.data;
                                const filterData = layerObject?.filters?.data;
                                // settings.visible indicates the filter can be configured by the user
                                // settings.selected indicates the default setting
                                const selectableFilters = filterData && filterData
                                    .filter(({ settings }) => (settings.visible === true))
                                    .map(({ settings }) => {
                                        return { settings: { ...settings, value: expandFilterValue(settings.value) } };
                                    });
                                return {
                                    themeId,
                                    customId: layerObject.id,
                                    id,
                                    title,
                                    filters: selectableFilters
                                };
                            })
                            .filter(({ filters }) => filters?.length > 0 )
                    }))
                    // remove the themes without layers with configurable filters
                    .filter(theme => theme.layers.length > 0)
                    .flatMap(({ layers }) => layers)
                    .sort(arraySortAscendingFunction('title'));
            },

            /**
              * Return the class for filter panel
              * @returns {string} - style
              */
            getObjectDetailClass() {
                return {
                    'show': this.filterPanel.visible,
                    'baselayer': this.$store.getters.getBaseLayers.length > 0
                };
            },
            $_getStyle() {
                const resize = this.filterPanel.resize;
                return resize ? `max-height: calc(100% - ${MENUBAR_HEIGHT}px - ${this.getDetailInformationPanelHeight}px - 10px)` : null;
            },
            hasSelectedFilters: function () {
                return (
                    this.appliedFilters.filter(
                        (filterObject) => filterObject?.filter?.selected
                    ).length > 0
                );
            }
        },

        methods: {
            ...mapActions(['togglePanel']),
            $_updatedFilter(data) {
                const currentFilter = this.appliedFilters.find(_filter => (
                    _filter.themeId === data.themeId &&
                    _filter.layerId === data.layerId &&
                    _filter.filterId === data.filter.id
                ));

                // a filter selection has been updated, create the configuration
                const appliedFilter = {
                    themeId: data.themeId,
                    layerId: data.layerId,
                    filterId: data.filter.id,
                    filter: data.filter
                };
                // skip if the value and selected did not change
                const hasSameValue = currentFilter &&
                    (
                        currentFilter.filter.value === appliedFilter.filter.value &&
                        currentFilter.filter.selected === appliedFilter.filter.selected
                    );
                if (hasSameValue) {
                    return;
                }

                // remove the current configuration of the filter in the local state
                const appliedFilters = this.appliedFilters.filter(_filter =>
                    !(_filter.themeId === appliedFilter.themeId && _filter.layerId === appliedFilter.layerId && _filter.filterId === appliedFilter.filterId)
                );
                // update the configuration of the filters in the local state including the updated one
                this.appliedFilters = [...appliedFilters, appliedFilter];
            },
            async $_applyFilters() {
                // applies filters on the layers, updates the (main) map
                await this.$store.dispatch('setFilters', { filters: this.appliedFilters });
                // the user can select a checkbox if he/she needs to create an selection from the current filters
                if (this.showFilterSelectionInPanel) {
                    if (this.hasSelectedFilters) {
                        if (this.getWebTheme.data.showTableView) {
                            this.$store.dispatch('createAndDisplaySelection', {
                                actionName: 'AddSelectionFromFilters',
                                actionSettings: { filters: this.appliedFilters },
                                type: SELECTION_TYPES.FILTERED
                            });
                        } else {
                            this.$store.dispatch('createAndDisplaySelectionFromFiltersInWebInfoPanel', { filters: this.appliedFilters });
                        }
                    } else {
                        this.$store.commit('ADD_STATUS_MESSAGE', {
                            message: this.$t('selectItemsWarning'),
                            variant: 'info',
                            autoHideDelay: 5000
                        });
                    }
                }
            }
        }
    };
</script>

<style scoped lang="scss">
    .card {
        border: 0px !important;
    }
    .filtercontainer {
        padding-top: 10px;
        margin-bottom: 15px;
        padding-left: 3px;
    }

    .panel-content-sidebar {
        flex: 1;
        display: flex !important;
        flex-direction: column;
        overflow: auto;
    }

    .filter-detail {
        @include z-index(menu-panel);
        position: absolute;
        top: $menu-bar-height;
        right: 100%;
        left: -470px;
        width: $default-panel-width;
        background-color: #fff;
        -moz-transition: all 0.3s ease-in-out;
        -o-transition: all 0.3s ease-in-out;
        transition: all 0.3s ease-in-out;
        display: flex !important;
        flex-direction: column;
        max-height: calc(100% - $menu-bar-height - 10px);

        &.show {
            @include margin-left-navbar;
            position: absolute;
            top: $menu-bar-height;
        }

        &.baselayer {
            max-height: calc(100% - 155px);
        }
    }

    /*-------------------------------------
    max-width : 480px
    ---------------------------------------*/
    @media (max-width: 480px) {
        .filter-detail.show {
            width: calc(100% - 20px);
        }
    }
</style>
